import { getLocalDateInputValue } from "@/lib/date";
import { frequencyLabel, weekdayLabel } from "@/data/reminders";

const minutesFromTime = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

const getWeekStart = (date) => {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  start.setDate(start.getDate() - start.getDay());
  return start;
};

const parseDate = (value) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const hasDailyReflection = (reflections, todayValue) =>
  reflections.some((reflection) => reflection.reflection_date === todayValue);

const hasWeeklyReflection = (reflections, now) => {
  const weekStart = getWeekStart(now);
  return reflections.some((reflection) => parseDate(reflection.reflection_date) >= weekStart);
};

const hasMonthlyReflection = (reflections, now) =>
  reflections.some((reflection) => {
    const reflectionDate = parseDate(reflection.reflection_date);
    return reflectionDate.getFullYear() === now.getFullYear() && reflectionDate.getMonth() === now.getMonth();
  });

const isPreferredTimeReached = (settings, now) =>
  now.getHours() * 60 + now.getMinutes() >= minutesFromTime(settings.preferred_time);

const isPreferredDayReached = (settings, now) => {
  if (settings.frequency === "weekly") return now.getDay() >= Number(settings.weekly_day);
  if (settings.frequency === "monthly") return now.getDate() >= Number(settings.monthly_day);
  return true;
};

const hasReflectionForFrequency = (settings, reflections, now) => {
  if (settings.frequency === "weekly") return hasWeeklyReflection(reflections, now);
  if (settings.frequency === "monthly") return hasMonthlyReflection(reflections, now);
  return hasDailyReflection(reflections, getLocalDateInputValue(now));
};

export const buildReminderStatus = (settings, reflections, now = new Date()) => {
  if (!settings?.enabled) {
    return { due: false, title: "Reminders are off", detail: "Turn reminders on to see reflection prompts here." };
  }

  const ready = isPreferredTimeReached(settings, now) && isPreferredDayReached(settings, now);
  const completed = hasReflectionForFrequency(settings, reflections, now);
  const label = frequencyLabel(settings.frequency).toLowerCase();

  if (ready && !completed) {
    return {
      due: true,
      title: `Time for your ${label} reflection`,
      detail: `Your reminder is set for ${settings.preferred_time}. Take a few moments to notice the Fruit of the Spirit.`,
    };
  }

  const dayText = settings.frequency === "weekly" ? ` on ${weekdayLabel(settings.weekly_day)}` : "";
  const monthText = settings.frequency === "monthly" ? ` on day ${settings.monthly_day}` : "";
  return {
    due: false,
    title: `${frequencyLabel(settings.frequency)} reminder is set`,
    detail: `We'll show a reflection reminder at ${settings.preferred_time}${dayText}${monthText}.`,
  };
};